import { useEffect, useState } from "react";
import { AlertTriangle, Check, Sparkles, X } from "lucide-react";
import { getProtectedImageBlob } from "services/cameraApi";
import { imageRotationClass } from "utils/cameraImage";
import { READER_TRUST_CONFIDENCE, RULER_MAX_CM, fieldLevel, truthOf } from "utils/groundTruth";
import { enhanceImageBlob } from "utils/enhanceImage";

const ENHANCE_STORAGE_KEY = "padihat_enhance_ruler";

// Per-browser convenience only - the page renders fine if storage is unavailable.
function readEnhancePreference() {
  try {
    return localStorage.getItem(ENHANCE_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

/**
 * Props: image, onClose, and optionally onLabel(imageId, trueLevelCm|null) => Promise - when given,
 * the dialog shows the "True water level" box so a person can record the real level read off the
 * photo (the ground truth the sensors are compared against). The Camera tab omits it. With it, the box also
 * shows the ruler reader's reading of the photo (image.ruler_reading): a person can accept it as the label
 * (it is then saved as their label) or type a correction. The reading itself is never changed.
 */
export default function CameraImageModal({ image, onClose, onLabel }) {
  const [url, setUrl] = useState("");
  const [blob, setBlob] = useState(null);
  const [error, setError] = useState("");
  const [enhance, setEnhance] = useState(readEnhancePreference);
  const [enhancedUrl, setEnhancedUrl] = useState("");
  const [enhancing, setEnhancing] = useState(false);
  const [enhanceError, setEnhanceError] = useState("");
  const [labelText, setLabelText] = useState("");
  const [labelBusy, setLabelBusy] = useState(false);
  const [labelError, setLabelError] = useState("");

  const toggleEnhance = () => {
    const next = !enhance;
    setEnhance(next);
    try {
      localStorage.setItem(ENHANCE_STORAGE_KEY, next ? "1" : "0");
    } catch {
      // not persisted - fine
    }
  };

  // Only the photo itself reloads the picture - saving a label gives the dialog a new image object
  // for the same photo, which must not refetch it.
  const imageUrl = image?.image_url;
  const savedLabel = image?.true_level_cm;
  const imageId = image?.id;
  const reading = image?.ruler_reading || null;
  const readerLevel = typeof reading?.level_cm === "number" ? reading.level_cm : null;

  // Pre-filled with the person's label, else the ruler reader's reading (so accepting it is one click).
  useEffect(() => {
    const start = typeof savedLabel === "number" ? savedLabel : readerLevel;
    setLabelText(typeof start === "number" ? String(start) : "");
    setLabelError("");
  }, [imageId, savedLabel, readerLevel]);

  const saveLabel = async (value) => {
    setLabelBusy(true);
    setLabelError("");
    try {
      await onLabel(imageId, value);
    } catch (requestError) {
      setLabelError(requestError.message || "Could not save the label.");
    } finally {
      setLabelBusy(false);
    }
  };

  const submitLabel = (event) => {
    event.preventDefault();
    const trimmed = labelText.trim();
    const value = Number(trimmed);
    if (trimmed === "" || !Number.isFinite(value) || value < 0 || value > RULER_MAX_CM) {
      setLabelError(`Enter the ruler reading in cm, between 0 and ${RULER_MAX_CM}.`);
      return;
    }
    saveLabel(value);
  };

  useEffect(() => {
    if (!imageUrl) return undefined;
    const controller = new AbortController();
    let objectUrl = "";
    getProtectedImageBlob(imageUrl, { signal: controller.signal })
      .then(({ blob: loaded }) => {
        objectUrl = URL.createObjectURL(loaded);
        setBlob(loaded);
        setUrl(objectUrl);
      })
      .catch((requestError) => {
        if (requestError.name !== "AbortError") setError(requestError.message);
      });
    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      setUrl("");
      setBlob(null);
      setError("");
    };
  }, [imageUrl]);

  // Enhanced copy of the loaded photo, built on demand and cached per image.
  // A display filter only: the stored photo is never changed.
  useEffect(() => {
    if (!enhance || !blob) return undefined;
    let cancelled = false;
    let objectUrl = "";
    setEnhancing(true);
    setEnhanceError("");
    enhanceImageBlob(blob)
      .then((result) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(result);
        setEnhancedUrl(objectUrl);
      })
      .catch((requestError) => {
        if (!cancelled) setEnhanceError(requestError.message || "Could not enhance this image.");
      })
      .finally(() => {
        if (!cancelled) setEnhancing(false);
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      setEnhancedUrl("");
    };
  }, [enhance, blob]);

  if (!image) return null;
  const labelled = typeof savedLabel === "number";
  const truth = truthOf(image);
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/75 p-4" role="dialog" aria-modal="true">
      <div className="flex max-h-[95vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex shrink-0 items-center justify-between border-b border-slate-200 px-4 py-3">
          <div>
            <h2 className="font-mono text-lg font-bold">Capture #{image.capture_id}</h2>
            <p className="text-xs text-slate-500">Received {new Date(image.server_received_at).toLocaleString()}</p>
          </div>
          <button onClick={onClose} aria-label="Close image" className="rounded-lg p-2 hover:bg-slate-100">
            <X className="h-5 w-5" />
          </button>
        </div>
        {/* Photo on the left, controls on the right, so labelling never needs a scroll. Stacks on a phone. */}
        <div data-testid="image-and-controls" className="flex min-h-0 flex-1 flex-col overflow-auto md:flex-row md:overflow-hidden">
          <div className="flex min-h-0 flex-1 items-center justify-center bg-slate-100 p-3">
            {url ? (
              <img
                src={enhance && enhancedUrl ? enhancedUrl : url}
                alt={`Full ruler capture ${image.capture_id}`}
                className={`max-h-[55vh] w-auto max-w-full object-contain md:max-h-[calc(95vh-7rem)] ${imageRotationClass(image.rotation_deg)}`}
              />
            ) : (
              <div className="flex aspect-[4/3] items-center justify-center text-slate-500">
                {error || "Loading authenticated image…"}
              </div>
            )}
          </div>
          <aside className="flex w-full shrink-0 flex-col gap-4 overflow-y-auto border-t border-slate-200 p-4 md:w-80 md:border-l md:border-t-0">
            {onLabel && (
              <form onSubmit={submitLabel} data-testid="label-form">
                <div className="flex items-center justify-between gap-2">
                  <label className="text-sm font-semibold text-slate-700" htmlFor="true-level-input">
                    True water level (cm on the ruler)
                  </label>
                  {truth?.counts && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-bold text-emerald-800" data-testid="label-badge">
                      <Check className="h-3 w-3" /> {truth.source === "human" ? "Labelled" : "Auto-labelled"}
                    </span>
                  )}
                  {truth?.review && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-bold text-amber-800" data-testid="label-badge">
                      <AlertTriangle className="h-3 w-3" /> Needs review
                    </span>
                  )}
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <input
                    id="true-level-input"
                    type="number"
                    min="0"
                    max={RULER_MAX_CM}
                    step="0.1"
                    value={labelText}
                    disabled={labelBusy}
                    onChange={(event) => setLabelText(event.target.value)}
                    className="w-24 rounded-lg border border-slate-200 px-2 py-1.5 text-sm"
                  />
                  <button
                    type="submit"
                    disabled={labelBusy}
                    className="rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-bold text-white disabled:opacity-50"
                  >
                    {labelBusy ? "Saving…" : "Save label"}
                  </button>
                  {labelled && (
                    <button
                      type="button"
                      disabled={labelBusy}
                      onClick={() => saveLabel(null)}
                      className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold text-slate-700 disabled:opacity-50"
                    >
                      Clear
                    </button>
                  )}
                </div>
                {labelled && (
                  <p className="mt-2 text-xs font-semibold text-slate-700" data-testid="label-field-level">
                    = {fieldLevel(savedLabel)} cm in the field
                  </p>
                )}
                {labelled && image.labeled_at && (
                  <p className="mt-0.5 text-xs text-slate-500">
                    Saved{image.labeled_by ? ` by ${image.labeled_by}` : ""} {new Date(image.labeled_at).toLocaleString()}
                  </p>
                )}
                {labelError && (
                  <p role="alert" className="mt-1 text-xs text-red-700">{labelError}</p>
                )}
                <div
                  data-testid="ruler-reader"
                  className={`mt-3 rounded-lg border p-2 text-xs ${
                    truth?.review ? "border-amber-300 bg-amber-50 text-amber-900" : "border-slate-200 bg-slate-50 text-slate-700"
                  }`}
                >
                  {!reading ? (
                    <p>Ruler reader: not read yet (it reads new photos every few minutes).</p>
                  ) : readerLevel === null ? (
                    <p>Ruler reader could not read this photo{reading.reason ? `: ${reading.reason}` : "."}</p>
                  ) : (
                    <>
                      <p>
                        <span className="font-semibold">Ruler reader: {readerLevel} cm</span> (= {reading.field_level_cm} cm in the
                        field) · confidence {Number(reading.confidence).toFixed(2)}
                        {reading.reader_version ? ` · v${reading.reader_version}` : ""}
                      </p>
                      {!labelled && truth?.review && (
                        <p className="mt-1">
                          Below {READER_TRUST_CONFIDENCE} confidence, so it does not count yet. Check the photo, then accept the
                          reading or type the correct one.
                        </p>
                      )}
                      {!labelled && truth?.counts && (
                        <p className="mt-1">Used as this photo's label until a person labels it.</p>
                      )}
                      {labelled && (
                        <p className="mt-1" data-testid="reader-difference">
                          {(() => {
                            const mm = Math.round((savedLabel - readerLevel) * 100) / 10;
                            if (mm === 0) return "Your label matches it.";
                            return `Your label is ${Math.abs(mm)} mm ${mm > 0 ? "higher" : "lower"} than the reader's.`;
                          })()}
                        </p>
                      )}
                      {!labelled && (
                        <button
                          type="button"
                          data-testid="accept-reading"
                          disabled={labelBusy}
                          onClick={() => saveLabel(readerLevel)}
                          className={`mt-2 rounded-lg px-3 py-1.5 text-xs font-bold disabled:opacity-50 ${
                            truth?.review ? "bg-amber-600 text-white" : "border border-slate-200 bg-white text-slate-700"
                          }`}
                        >
                          Accept {readerLevel} cm
                        </button>
                      )}
                    </>
                  )}
                </div>
                <p className="mt-2 text-xs text-slate-500">
                  Read the water surface against one reference scale (the tape on the glass) and use that same
                  scale for every photo - the two scales can differ by a few millimetres. Type the reading as it
                  is on the ruler; the chart shows it as field level (the 30 cm mark is ground, below it negative).
                </p>
              </form>
            )}

            {url && (
              <div className="text-xs text-slate-600">
                <button
                  type="button"
                  onClick={toggleEnhance}
                  aria-pressed={enhance}
                  className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 font-semibold ${
                    enhance ? "border-blue-600 bg-blue-600 text-white" : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  <Sparkles className="h-3.5 w-3.5" /> Enhance ruler
                </button>
                <p className="mt-1">
                  {enhancing
                    ? "Enhancing…"
                    : enhanceError
                    ? `Couldn't enhance: ${enhanceError}`
                    : enhance && enhancedUrl
                    ? "Showing the enhanced view (black & white, lighting evened out, sharpened). The stored photo is unchanged."
                    : "Evens out the lighting and sharpens, to make the ruler's ticks easier to count."}
                </p>
              </div>
            )}

            <dl className="grid grid-cols-2 gap-3 text-sm">
              <div><dt className="text-slate-500">Trigger</dt><dd>{image.capture_trigger || "—"}</dd></div>
              <div><dt className="text-slate-500">Wake</dt><dd>{image.wake_reason || "—"}</dd></div>
              <div><dt className="text-slate-500">Reset</dt><dd>{image.reset_reason || "—"}</dd></div>
              <div><dt className="text-slate-500">Telemetry</dt><dd>{image.telemetry_id || "Not linked"}</dd></div>
              <div className="col-span-2"><dt className="text-slate-500">CRC32</dt><dd className="break-all font-mono text-xs">{image.crc32}</dd></div>
            </dl>
          </aside>
        </div>
      </div>
    </div>
  );
}
